import { Ionicons } from "@expo/vector-icons";
import React, { useEffect, useMemo, useState } from "react";
import {
    FlatList,
    Modal,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

interface SelectionModalProps {
  visible: boolean;
  title: string;
  data: string[];
  selectedValue: string;
  onSelect: (value: string) => void;
  onClose: () => void;
  // --- Options facultatives (rétrocompatibles) ---
  // Affiche une barre de recherche qui filtre `data` (et `secondaryData`) au fil
  // de la frappe, insensible à la casse et aux accents.
  searchable?: boolean;
  searchPlaceholder?: string;
  // Section secondaire affichée sous un titre (ex. « Autre canton »).
  secondaryData?: string[];
  secondaryTitle?: string;
  // Ajout d'une nouvelle valeur : si la recherche ne correspond à aucune entrée
  // existante, une ligne « Ajouter … » apparaît et appelle `onAddOption`.
  onAddOption?: (query: string) => void;
  addOptionLabel?: (query: string) => string;
  // Texte affiché quand aucun résultat et qu'aucun ajout n'est possible.
  emptyText?: string;
}

// Normalisation pour un filtrage insensible à la casse et aux accents.
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function SelectionModal({
  visible,
  title,
  data,
  selectedValue,
  onSelect,
  onClose,
  searchable = false,
  searchPlaceholder,
  secondaryData,
  secondaryTitle,
  onAddOption,
  addOptionLabel,
  emptyText,
}: SelectionModalProps) {
  const [query, setQuery] = useState("");

  // Réinitialise la recherche à chaque ouverture pour ne pas conserver un
  // filtrage d'une sélection précédente.
  useEffect(() => {
    if (visible) setQuery("");
  }, [visible]);

  const handleSelect = (item: string) => {
    onSelect(item);
    onClose();
  };

  const normalizedQuery = normalize(query);

  const filteredData = useMemo(() => {
    if (!searchable || !normalizedQuery) return data;
    return data.filter((item) => normalize(item).includes(normalizedQuery));
  }, [data, searchable, normalizedQuery]);

  const filteredSecondary = useMemo(() => {
    if (!secondaryData) return [];
    if (!searchable || !normalizedQuery) return secondaryData;
    return secondaryData.filter((item) =>
      normalize(item).includes(normalizedQuery)
    );
  }, [secondaryData, searchable, normalizedQuery]);

  const trimmedQuery = query.trim();
  const hasExactMatch = useMemo(() => {
    if (!trimmedQuery) return false;
    const q = normalize(trimmedQuery);
    return (
      data.some((item) => normalize(item) === q) ||
      (secondaryData ?? []).some((item) => normalize(item) === q)
    );
  }, [data, secondaryData, trimmedQuery]);

  const canAdd =
    !!onAddOption && !!addOptionLabel && trimmedQuery.length > 0 && !hasExactMatch;

  const handleAdd = () => {
    if (!onAddOption) return;
    onAddOption(trimmedQuery);
    onClose();
  };

  const renderItem = ({ item }: { item: string }) => {
    const isSelected = item === selectedValue;
    return (
      <TouchableOpacity
        style={[styles.item, isSelected && styles.itemSelected]}
        onPress={() => handleSelect(item)}
      >
        <Text style={[styles.itemText, isSelected && styles.itemTextSelected]}>
          {item}
        </Text>
        {isSelected && <Ionicons name="checkmark" size={20} color="#1C453F" />}
      </TouchableOpacity>
    );
  };

  const showEmpty =
    filteredData.length === 0 && filteredSecondary.length === 0 && !canAdd;

  const renderFooter = () => (
    <View>
      {filteredSecondary.length > 0 && (
        <View>
          {secondaryTitle ? (
            <Text style={styles.sectionTitle}>{secondaryTitle}</Text>
          ) : null}
          {filteredSecondary.map((item) => {
            const isSelected = item === selectedValue;
            return (
              <TouchableOpacity
                key={`secondary-${item}`}
                style={[styles.item, isSelected && styles.itemSelected]}
                onPress={() => handleSelect(item)}
              >
                <Text
                  style={[styles.itemText, isSelected && styles.itemTextSelected]}
                >
                  {item}
                </Text>
                {isSelected && (
                  <Ionicons name="checkmark" size={20} color="#1C453F" />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {canAdd && addOptionLabel && (
        <TouchableOpacity style={styles.addItem} onPress={handleAdd}>
          <Ionicons name="add-circle-outline" size={20} color="#1C453F" />
          <Text style={styles.addItemText}>{addOptionLabel(trimmedQuery)}</Text>
        </TouchableOpacity>
      )}

      {showEmpty && emptyText ? (
        <Text style={styles.emptyText}>{emptyText}</Text>
      ) : null}
    </View>
  );

  return (
    <Modal
      animationType="slide"
      transparent={false}
      visible={visible}
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.container} edges={["top", "bottom"]}>
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={28} color="#000" />
          </TouchableOpacity>
        </View>

        {searchable && (
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={18} color="#999" />
            <TextInput
              style={styles.searchInput}
              value={query}
              onChangeText={setQuery}
              placeholder={searchPlaceholder}
              placeholderTextColor="#999"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="search"
            />
            {query.length > 0 && (
              <TouchableOpacity onPress={() => setQuery("")}>
                <Ionicons name="close-circle" size={18} color="#999" />
              </TouchableOpacity>
            )}
          </View>
        )}

        <FlatList
          data={filteredData}
          renderItem={renderItem}
          keyExtractor={(item) => item}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.listContent}
          ListFooterComponent={renderFooter}
        />
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
    flex: 1,
  },
  closeButton: {
    padding: 8,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "#F5F5F5",
    borderRadius: 12,
  },
  searchInput: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    padding: 0,
  },
  listContent: {
    padding: 16,
  },
  item: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemSelected: {
    backgroundColor: "#F0F8F5",
  },
  itemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#333",
    flex: 1,
  },
  itemTextSelected: {
    fontFamily: "Inter_600SemiBold",
    color: "#1C453F",
  },
  sectionTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 13,
    color: "#999",
    textTransform: "uppercase",
    marginTop: 24,
    marginBottom: 8,
    paddingHorizontal: 12,
  },
  addItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 12,
    marginTop: 8,
  },
  addItemText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#1C453F",
    flex: 1,
  },
  emptyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 15,
    color: "#999",
    textAlign: "center",
    paddingVertical: 24,
    paddingHorizontal: 12,
  },
});
